import * as React from "react";
import {
  Box,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import CardBase from "../../components/global/CardBase";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useTitle } from "../../hooks/useTitle";
import BarChartSurveyAnswer from "./BarChartSurveyAnswer";
import DonutChartTalent from "./DonutChartTalent";
import DonutChartCustomer from "./DonutChartCustomer";
import DonutChartJob from "./DonutChartJob";
import DonutChartUser from "./DonutChartUser";
import FinanceActivitiesPanel from "./FinanceActivitiesPanel";
import ProjectNewsPanel from "./ProjectNewsPanel";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "react-router-dom";
import DailyNewUserCard from "./DailyNewUserCard";
import ModalStatisticSkillByTalent from "./ModalStatisticSkillByTalent";
import ModalStatisticSkillByProject from "./ModalStatisticSkillByProject";
import ModalStatisticVerticalByCompany from "./ModalStatisticVerticalByCompany";
import ProjectSummaryCard from "./ProjectSummaryCard";
import { isManage360 } from "../../utils/helper";
import { CARD_PREFERENCE_TYPE, CARD_PREFERENCE_PAGE } from "../../constants/cardPreferences";
import DashboardCustomizeLayoutModal from "../../components/DashboardCustomizeLayoutModal";
import { useCardPreferences } from "../../hooks/useCardPreferences";
import { DASHBOARD_CARD_IDS, DEFAULT_DASHBOARD_CARD_ORDER } from "../../constants/dashboardCards";

// Global constants for card preferences
export const CARD_PREFERENCE_TYPE_ADMIN = CARD_PREFERENCE_TYPE.ADMIN;
export const CARD_PREFERENCE_PAGE_DASHBOARD = CARD_PREFERENCE_PAGE.DASHBOARD;

type CustomCardProps = {
  title: string;
  page: string;
  children?: React.ReactNode;
};

const CustomCard: React.FC<CustomCardProps> = ({ title, page, children }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleOpen = () => {
    navigate(page);
    handleClose();
  };

  return (
    <CardBase
      title={title}
      sx={{ height: "100%" }}
      useGlassCard={false}
      headerActions={
        <>
          <IconButton aria-label="more" onClick={handleClick} size="small">
            <MoreVertIcon />
          </IconButton>
          <Menu
            sx={{ mt: "45px" }}
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem key={"detail"} onClick={handleOpen}>
              <Typography textAlign="center">More Detail</Typography>
            </MenuItem>
          </Menu>
        </>
      }
    >
      {children}
    </CardBase>
  );
};

const DashboardPage = () => {
  useTitle("Admin Dashboard");
  const user = useCurrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [showModalTalentSkill, setShowModalTalentSkill] = React.useState(false);
  const [showModalProjectSkill, setShowModalProjectSkill] =
    React.useState(false);
  const [showModalCompanyIndustry, setShowModalCompanyIndustry] =
    React.useState(false);
  const [showCustomizeLayout, setShowCustomizeLayout] = React.useState(false);
  const _isManage360 = React.useMemo(() => isManage360(), []);

  // Get card preferences
  const { preferences, isLoading: isLoadingPreferences, refetch } = useCardPreferences({
    type: CARD_PREFERENCE_TYPE.ADMIN,
    page: CARD_PREFERENCE_PAGE.DASHBOARD,
    skip: false,
  });

  // Store preferences in state to persist during refetch (prevent glitch)
  const [cachedPreferences, setCachedPreferences] = React.useState(preferences);

  // Update cached preferences when new preferences are loaded
  React.useEffect(() => {
    if (preferences) {
      setCachedPreferences(preferences);
    }
  }, [preferences]);

  // Get card order and hidden cards from cached preferences (or current preferences)
  const activePreferences = cachedPreferences || preferences;
  const cardOrder = React.useMemo(() => {
    if (activePreferences && activePreferences.cardOrder && activePreferences.cardOrder.length > 0) {
      // Filter out hidden cards and add any missing cards
      const allCardIds = Object.values(DASHBOARD_CARD_IDS);
      const filteredOrder = activePreferences.cardOrder.filter(
        (id: string) => allCardIds.includes(id as any)
      );
      const missingCards = DEFAULT_DASHBOARD_CARD_ORDER.filter(
        (id: string) => !filteredOrder.includes(id)
      );
      return [...filteredOrder, ...missingCards];
    }
    return DEFAULT_DASHBOARD_CARD_ORDER;
  }, [activePreferences]);

  const hiddenCards = React.useMemo(() => {
    return activePreferences?.hiddenCards || [];
  }, [activePreferences]);

  // Refetch preferences when modal closes
  React.useEffect(() => {
    if (!showCustomizeLayout) {
      refetch();
    }
  }, [showCustomizeLayout, refetch]);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: "1rem", sm: "1.25rem" },
            fontWeight: 600,
          }}
        >
          Welcome Back, {user?.first_name} 👋
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setShowCustomizeLayout(true)}
          size={isMobile ? "small" : "medium"}
          startIcon={<SettingsIcon />}
          sx={{
            width: { xs: "100%", sm: "auto" },
            color: "#F4A259",
            borderColor: "#F4A259",
            "&:hover": {
              borderColor: "#F4A259",
              backgroundColor: "rgba(244, 162, 89, 0.1)",
            },
          }}
        >
          Customize Layout
        </Button>
      </Stack>
      {isLoadingPreferences && !cachedPreferences ? (
        // Show skeleton loading only on initial load (no cached data)
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 1.5, sm: 2 } }}>
          {DEFAULT_DASHBOARD_CARD_ORDER.slice(0, 4).map((_, index) => (
            <Grid key={`skeleton-${index}`} item xs={12} sm={6} md={4}>
              <CardBase useGlassCard={false} sx={{ height: "200px" }}>
                <Skeleton variant="rectangular" width="100%" height="100%" />
              </CardBase>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 1.5, sm: 2 } }}>
          {/* Render all cards in order from preferences */}
          {cardOrder
            .filter((cardId: string) => !hiddenCards.includes(cardId))
            .map((cardId: string) => {
              // Map card IDs to components
              switch (cardId) {
                case DASHBOARD_CARD_IDS.newTalentsClients:
                  return (
                    <Grid key={cardId} item xs={12} sm={8}>
                      <DailyNewUserCard />
                    </Grid>
                  );
                case DASHBOARD_CARD_IDS.summary:
                  return !_isManage360 ? (
                    <Grid key={cardId} item xs={12} sm={4}>
                      <ProjectSummaryCard />
                    </Grid>
                  ) : null;
                case DASHBOARD_CARD_IDS.moreStats:
                  return (
                    <Grid key={cardId} item xs={12}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        spacing={{ xs: 1, sm: 0 }}
                        sx={{ flexWrap: { xs: "nowrap", sm: "wrap" } }}
                      >
                        <Typography
                          variant="button"
                          sx={{
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            textAlign: { xs: "center", sm: "left" },
                          }}
                        >
                          More Stats {!isMobile && "⇒"}
                        </Typography>
                        <Button
                          sx={{
                            ml: { xs: 0, sm: 1 },
                            mr: { xs: 0, sm: 1 },
                            mb: { xs: 0.5, sm: 0 },
                            width: { xs: "100%", sm: "auto" },
                          }}
                          onClick={() => setShowModalTalentSkill(true)}
                          size={isMobile ? "small" : "medium"}
                        >
                          Skill by Talent
                        </Button>
                        <Button
                          sx={{
                            mr: { xs: 0, sm: 1 },
                            mb: { xs: 0.5, sm: 0 },
                            width: { xs: "100%", sm: "auto" },
                          }}
                          onClick={() => setShowModalProjectSkill(true)}
                          size={isMobile ? "small" : "medium"}
                        >
                          Skill by Project
                        </Button>
                        <Button
                          sx={{
                            width: { xs: "100%", sm: "auto" },
                          }}
                          onClick={() => setShowModalCompanyIndustry(true)}
                          size={isMobile ? "small" : "medium"}
                        >
                          Industry by Company
                        </Button>
                      </Stack>
                    </Grid>
                  );
                case DASHBOARD_CARD_IDS.jobs:
                  return (
                    <Grid key={cardId} item xs={12} sm={6} md={4}>
                      <CustomCard title="Jobs" page="/jobs">
                        <DonutChartJob id="chart-job" />
                      </CustomCard>
                    </Grid>
                  );
                case DASHBOARD_CARD_IDS.surveyAnswer:
                  return (
                    <Grid key={cardId} item xs={12} sm={6} md={8}>
                      <CardBase
                        title="How do people know Workz360?"
                        sx={{ height: "100%" }}
                        useGlassCard={false}
                      >
                        <BarChartSurveyAnswer id="chart-survey-answer" />
                      </CardBase>
                    </Grid>
                  );
                case DASHBOARD_CARD_IDS.talents:
                  return (
                    <Grid key={cardId} item xs={12} sm={6} md={4}>
                      <CustomCard title="Talents" page="/talents">
                        <DonutChartTalent id="chart-talent" />
                      </CustomCard>
                    </Grid>
                  );
                case DASHBOARD_CARD_IDS.clients:
                  return (
                    <Grid key={cardId} item xs={12} sm={6} md={4}>
                      <CustomCard title="Clients" page="/clients">
                        <DonutChartCustomer id="chart-customer" />
                      </CustomCard>
                    </Grid>
                  );
                case DASHBOARD_CARD_IDS.adminUsers:
                  return (
                    <Grid key={cardId} item xs={12} sm={6} md={4}>
                      <CustomCard title="Admin Users" page="/admins">
                        <DonutChartUser id="chart-user" />
                      </CustomCard>
                    </Grid>
                  );
                case DASHBOARD_CARD_IDS.newestProjects:
                  return (
                    <Grid key={cardId} item xs={12} sm={6}>
                      <CardBase title="Newest Projects" useGlassCard={false}>
                        <ProjectNewsPanel />
                      </CardBase>
                    </Grid>
                  );
                case DASHBOARD_CARD_IDS.financeActivities:
                  return !_isManage360 ? (
                    <Grid key={cardId} item xs={12} sm={6}>
                      <CardBase title="Finance Activities" useGlassCard={false}>
                        <FinanceActivitiesPanel />
                      </CardBase>
                    </Grid>
                  ) : null;
                default:
                  return null;
              }
            })}
        </Grid>
      )}
      <ModalStatisticSkillByTalent
        open={showModalTalentSkill}
        onClose={() => setShowModalTalentSkill(!showModalTalentSkill)}
      />
      <ModalStatisticSkillByProject
        open={showModalProjectSkill}
        onClose={() => setShowModalProjectSkill(!showModalProjectSkill)}
      />
      <ModalStatisticVerticalByCompany
        open={showModalCompanyIndustry}
        onClose={() => setShowModalCompanyIndustry(!showModalCompanyIndustry)}
      />
      <DashboardCustomizeLayoutModal
        open={showCustomizeLayout}
        onClose={() => setShowCustomizeLayout(false)}
      />
    </Box>
  );
};
export default DashboardPage;
